import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { ShownCalendar } from "@portal/schemas";
import { Button, Checkbox, Popover, PopoverContent, PopoverTrigger } from "@portal/ui";
import { SlidersHorizontal } from "lucide-react";
import { useState } from "react";
import { updateCalendarSelection } from "../../api/client";
import { useCalendarList } from "../../api/queries";
import { InlineError } from "../../components/InlineError";
import { Hint } from "../../components/Hint";
import { calendarColor } from "./calendar-color";

/**
 * 表示するカレンダーを選ぶ(SPEC §6.5)。選べるのは、会社のアカウントの Google カレンダーにあるカレンダー
 * (自分のもの・共有されたもの)。たとえば、個人アカウントのカレンダーを会社のアカウントへ共有しておけば、
 * ここで選んで重ねて表示できる。選ぶとすぐ保存し、今日の予定を取り直す。保存するのは ID だけ。
 */
export function CalendarPicker({ shown }: { shown: readonly ShownCalendar[] }) {
  const [open, setOpen] = useState(false);
  const list = useCalendarList(open);
  const queryClient = useQueryClient();
  const save = useMutation({
    mutationFn: (calendarIds: string[]) => updateCalendarSelection({ calendarIds }),
    onSuccess: (data) => {
      queryClient.setQueryData(["calendar", "list"], data);
      void queryClient.invalidateQueries({ queryKey: ["calendar", "week"] });
    },
  });
  const calendars = list.data?.status === "connected" ? list.data.calendars : [];
  const selectedIds = calendars.filter((c) => c.selected).map((c) => c.id);

  const toggle = (id: string, checked: boolean) => {
    const next = checked ? [...selectedIds, id] : selectedIds.filter((x) => x !== id);
    if (next.length === 0) return; // 1 つは表示しておく
    save.mutate(next);
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          aria-label="表示するカレンダーを選ぶ"
          title="表示するカレンダーを選ぶ"
        >
          <SlidersHorizontal />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-80 text-sm">
        <div className="flex flex-col gap-3">
          <div className="flex items-center gap-1.5">
            <p className="font-semibold">表示するカレンダー</p>
            <Hint label="表示するカレンダーの説明">
              自分のカレンダーと、共有されたカレンダーから選べます。選んだものを重ねて表示します。個人のカレンダーなどは、Google
              カレンダー側で会社のアカウントへ共有すると、ここに出ます。
            </Hint>
          </div>
          {list.isPending && <p className="text-muted-foreground">読み込み中…</p>}
          {list.isError && (
            <p role="alert" className="text-destructive">
              カレンダーの一覧を取得できませんでした。
            </p>
          )}
          {calendars.length > 0 && (
            <ul aria-label="カレンダー" className="flex max-h-72 flex-col overflow-y-auto">
              {calendars.map((c) => {
                const onlyOne = c.selected && selectedIds.length === 1;
                return (
                  <li key={c.id}>
                    <label className="flex cursor-pointer items-center gap-2 py-1.5">
                      <Checkbox
                        checked={c.selected}
                        disabled={save.isPending || onlyOne}
                        onCheckedChange={(v) => toggle(c.id, v === true)}
                        aria-label={c.name}
                      />
                      <span
                        aria-hidden="true"
                        className="inline-block size-3 shrink-0"
                        style={{ backgroundColor: calendarColor(c) }}
                      />
                      <span className="min-w-0 flex-1 truncate">{c.name}</span>
                      {c.accessRole === "FREE_BUSY" && (
                        <span className="shrink-0 text-xs text-muted-foreground">
                          予定の有無のみ
                        </span>
                      )}
                    </label>
                  </li>
                );
              })}
            </ul>
          )}
          <InlineError error={save.error} />
          {shown.some((c) => c.failed) && (
            <p className="text-xs text-muted-foreground">
              一部のカレンダーを取得できませんでした(共有が外れた可能性があります)。
            </p>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
